import React, { memo, useMemo, useEffect, useState, useCallback } from "react";
import PropTypes from 'prop-types';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';
import { useLocalStore } from "../Hooks/useLocalStore";
import { useUrlParams } from "../Hooks/useUrlParams";
import { useTendencijosData } from "../Hooks/Hooks";
import CategoryScores from './TendencijosTable/CategoryScores';
import QuestionItem from './TendencijosTable/QuestionItem';
import PageHeader from './TendencijosTable/PageHeader';
import { checkObject } from './TendencijosTable/utils';
import { useRezultatai } from "../Hooks/useRezultatai";

// Memoizuotas visas komponentas siekiant išvengti bereikalingų perrenderiavimų
const TendencijosTable = memo(({
    tipai = [], 
    info = [], 
    pilnaInfo = [], 
    filtrai = {}, 
    filtraiMain = {},
    loading = false,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { search } = useUrlParams();
    const { setData: setTipas } = useLocalStore('tipas');
    const { setData: setKlausimas } = useLocalStore('klausimas');
    const [commonResults, setCommonResults] = useState(null);
    
    const {data: bendriResultatai, isLoading: isResultsLoading } = useRezultatai({
        formosTipas: filtraiMain.type,
        kasPildo: filtraiMain.kasPildo,
        metai: filtraiMain.metai
    });

    useEffect(() => {
        if (isResultsLoading) return;

        setCommonResults(bendriResultatai);
    }, [isResultsLoading, bendriResultatai, setCommonResults]);
    
    // Duomenų gavimas optimizuotas su datų formatavimo pagerinimais
    // Pastaba: duomenų filtracija perkelta į serverio pusę per useBendriRezultatai
    const { data, loading: dataLoading } = useTendencijosData(tipai, info, pilnaInfo, filtraiMain);
    const kasPildo = useMemo(() => filtraiMain?.kasPildo, [filtraiMain]);
    
    // Nerodyti tekstai tik perskaičiuojami, kai keičiasi kalba
    const nerodyti = useMemo(() => ({
        pages: t('pslNerodo'),
        questions: t('questionNerodyti')
    }), [t]);

    // Optimizuotas duomenų saugumo patikrinimas
    const safeData = useMemo(() => {
        const baseData = { ...data };
        
        // Jei yra bendri rezultatai, naudojame juos vietoj skaičiavimų fronte
        if (commonResults && commonResults.rezultatai) {
            return {
                ...baseData,
                visiNauji: baseData.visiNauji || {},
                // Čia naudojame bendrus rezultatus įmonių palyginimui
                visiNaujiBendri: commonResults.rezultatai
            };
        }
        
        // Jei nėra bendrų rezultatų, gražiname tuščius duomenis
        return {
            ...baseData,
            visiNauji: baseData.visiNauji || {},
            visiNaujiBendri: {}
        };
    }, [data, commonResults]);

    // Šiuo atveju, commonResults egzistavimas yra lemiamas veiksnys, ar rodyti palyginimą
    const showComparison = useMemo(() => {
        // Visada rodome palyginimą, jei yra bendri rezultatai
        if (commonResults && commonResults.rezultatai) {
            return true;
        }
        
        // Kitaip, sekame standartinius filtrus
        return Boolean(checkObject(filtrai) || filtraiMain?.imonesPavadinimas);
    }, [filtrai, filtraiMain?.imonesPavadinimas, commonResults]);

    // Callback funkcija, memoizuota, kad nepersidarytų per kiekvieną render
    const handleTitleClick = useCallback((titleName) => {
        setTipas(kasPildo);
        setKlausimas(titleName.substring(0, titleName.length - 1));
        // Navigation with state to preserve company selection
        navigate({ 
            pathname: "/Rezultatai", 
            search 
        }, {
            state: {
                filter: {
                    kasPildo: kasPildo,
                    imonesPavadinimas: filtraiMain?.imonesPavadinimas
                }
            }
        });
    }, [kasPildo, setTipas, setKlausimas, navigate, search, filtraiMain]);

    // Bendro įvertinimo atvaizdavimo komponentas
    const TotalScoreComponent = useMemo(() => {
        if (!safeData.visiNauji || !Object.keys(safeData.visiNauji).length) {
            return null;
        }

        return (
            <div className="question">
                <div className="title">
                    <p>{t('bendrasApklausosIvertinimas')}</p>
                    {commonResults && commonResults.lastUpdated && (
                        <small className="bendriRezInfo">
                            {t('Palyginimo duomenys atnaujinti:')} {new Date(commonResults.lastUpdated).toLocaleDateString()}
                        </small>
                    )}
                </div>
                {Object.keys(safeData.visiNauji).map(category => (
                    <CategoryScores
                        key={category}
                        mainValues={safeData.visiNauji}
                        comparisonValues={safeData.visiNaujiBendri}
                        category={category}
                        showComparison={showComparison}
                        t={t}
                        usePreCalculatedData={true}
                    />
                ))}
            </div>
        );
    }, [safeData.visiNauji, safeData.visiNaujiBendri, showComparison, t, commonResults]);

    // Loading/error būsenų atvaizdavimas
    const isLoading = useMemo(() => 
        loading || isResultsLoading || dataLoading,
        [loading, isResultsLoading, dataLoading]
    );
    
    if (isLoading) {
        return <CircularProgress />;
    }
    
    if (!data) {
        return <div>{t('noDataAvailable')}</div>;
    }

    const { 
        pavadinimai = [], 
        puslapiai = {}, 
        pagalMetus = {},
        visi = {},
        visiPilni = {}
    } = data;

    if (!pavadinimai || !Array.isArray(pavadinimai)) {
        return <div>{t('invalidData')}</div>;
    }

    const years = Object.keys(pagalMetus);

    return (
        <div key={'table-tendencijos-' + (info?.length || 0)} className="TendencijosTable">
            <div className="pageContent">
                {TotalScoreComponent}
            </div>
            {pavadinimai.map((pageName, pageIndex) => {
                // Skip pages that should not be shown or have no questions
                if (nerodyti.pages.includes(pageName) || !puslapiai[pageName] || !Array.isArray(puslapiai[pageName])) {
                    return null;
                }
                
                // Check if any questions in this page have data
                const pageHasData = puslapiai[pageName].some(title => {
                    if (!title || !title.name || nerodyti.questions.includes(title.name)) return false;
                    
                    return years.some(year => {
                        return visi[year]?.[title.name] !== undefined && 
                               visi[year]?.[title.name] !== null && 
                               visi[year]?.[title.name] !== '' && 
                               visi[year]?.[title.name] !== '-' && 
                               visi[year]?.[title.name] !== 0;
                    });
                });
                
                // Skip pages with no data
                if (!pageHasData) return null;
                
                return (
                    <div key={`${pageName}-${pageIndex}-table`} className="qeastionPage">
                        <div className="pageContent">
                            <PageHeader
                                title={pageName}
                                years={years}
                                t={t}
                            />
                            {puslapiai[pageName].map((title, titleIndex) => {
                                // Skip questions that should not be shown
                                if (!title || !title.name || nerodyti.questions.includes(title.name)) return null;
                                
                                // Check if this question has any data in any year
                                const hasAnyData = years.some(year => {
                                    return visi[year]?.[title.name] !== undefined && 
                                           visi[year]?.[title.name] !== null && 
                                           visi[year]?.[title.name] !== '' && 
                                           visi[year]?.[title.name] !== '-' && 
                                           visi[year]?.[title.name] !== 0;
                                });
                                
                                // Skip questions that have no data in any year
                                if (!hasAnyData) return null;
                                
                                return (
                                    <QuestionItem
                                        key={`question-${pageName}-${title.name}-${titleIndex}`}
                                        title={title}
                                        years={years}
                                        visi={visi}
                                        visiPilni={visiPilni}
                                        showComparison={showComparison}
                                        onTitleClick={handleTitleClick}
                                        t={t}
                                        usePreCalculatedData={!!commonResults}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

TendencijosTable.propTypes = {
    tipai: PropTypes.array,
    info: PropTypes.array.isRequired,
    pilnaInfo: PropTypes.array,
    filtrai: PropTypes.object,
    filtraiMain: PropTypes.object,
    loading: PropTypes.bool,
    hasBendriRezultatai: PropTypes.bool
};

// Pridedam displayName geresniam debugginimui
TendencijosTable.displayName = 'TendencijosTable';

export default TendencijosTable;